import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  ReactNode,
} from "react";
import api from "../../api/api"; // Axios instance for API calls
import { PaginationInfo } from "../../types/api";

interface RemindersContextType {
  reminders: any[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchReminders: (page?: number, perPage?: number) => Promise<void>;
  addReminder: (reminder: any) => Promise<void>;
  editReminder: (reminder: any) => Promise<void>;
  deleteReminder: (id: number | string) => Promise<void>;
}

export const RemindersContext = createContext<RemindersContextType | undefined>(
  undefined,
);

interface RemindersProviderProps {
  children: ReactNode;
}

export const useRemindersContext = (): RemindersContextType => {
  const context = useContext(RemindersContext);
  if (!context) {
    throw new Error("useRemindersContext must be used within RemindersProvider");
  }
  return context;
};

const RemindersProvider: React.FC<RemindersProviderProps> = ({ children }) => {
  const [reminders, setReminders] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchReminders = useCallback(async (page: number = 1, perPage: number = 15) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/reminders?${params.toString()}`);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let remindersData = [];
      let paginationData = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          remindersData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          remindersData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          remindersData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        remindersData = responseData.data;
      } else if (Array.isArray(responseData)) {
        remindersData = responseData;
      }
      
      setReminders(remindersData);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch reminders:", error);
      setReminders([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const addReminder = async (reminder: any): Promise<void> => {
    try {
      const response = await api.post("/reminders", reminder);
      const newReminder = response.data.data || response.data;
      setReminders((prev) => [...prev, newReminder]);
    } catch (error) {
      console.error("Failed to add reminder:", error);
      throw error;
    }
  };

  const editReminder = async (reminder: any): Promise<void> => {
    try {
      const response = await api.put(`/reminders/${reminder.id}`, reminder);
      const updatedReminder = response.data.data || response.data;
      setReminders((prev) =>
        prev.map((item) => (item.id === reminder.id ? updatedReminder : item))
      );
    } catch (error) {
      console.error("Failed to edit reminder:", error);
      throw error;
    }
  };

  const deleteReminder = async (id: number | string): Promise<void> => {
    try {
      await api.delete(`/reminders/${id}`);
      setReminders((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Failed to delete reminder:", error);
      throw error;
    }
  };

  useEffect(() => {
    fetchReminders(1, 15);
  }, [fetchReminders]);

  return (
    <RemindersContext.Provider
      value={{
        reminders,
        pagination,
        loading,
        fetchReminders,
        addReminder,
        editReminder,
        deleteReminder,
      }}
    >
      {children}
    </RemindersContext.Provider>
  );
};

export default RemindersProvider;
